// Shared "new contact" form (2026-07-23). Extracted verbatim from the
// ClientContactsTab inline form so BOTH the Contacts tab AND the presentation
// send window's "Add this person as a contact" popup render the SAME fields
// through ONE component — no parallel form (CEO 2026-07-23: contacts have many
// required fields, so the full form is the point; a silent quick-save was
// explicitly rejected). Owns its create mutation + query invalidation so there
// is a single definition of the contact-create wiring.
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BUILDING_EMAIL_TAG, isBuildingEmail, linksChanged, type LinkableAccount } from "@merchd/shared";
import { AlsoWorksWith } from "@/components/contacts/AlsoWorksWith";
import { DeleteEntityAction } from "@/components/common/DeleteEntityAction";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ContactLocationSelect, emptyLocation, locationPayload, useClientLocations } from "@/components/contacts/ContactLocationSelect";

export interface CreatedContact {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  title: string | null;
  isPrimary: boolean | null;
  tags?: string[] | null;
  /** The account it landed on — a location when added to a main account with
   *  locations (Bozzuto). */
  clientId?: string;
}

export function ContactForm(props: {
  clientId: string;
  /** Heading over the fields. Defaults to "New contact". */
  heading?: string;
  /** Prefill (e.g. the popup seeds the email the rep just typed). */
  initialFirst?: string;
  initialLast?: string;
  initialEmail?: string;
  initialTitle?: string;
  /** Edit this existing contact instead of creating one (Stephanie
   *  2026-10-14: there was no way to fix a contact once added). */
  contact?: CreatedContact & { phone: string | null };
  onCreated: (contact: CreatedContact) => void;
  onCancel: () => void;
}) {
  const {
    clientId,
    contact,
    heading = contact ? "Edit contact" : "New contact",
    initialFirst = "",
    initialLast = "",
    initialEmail = "",
    initialTitle = "",
    onCreated,
    onCancel,
  } = props;

  const queryClient = useQueryClient();
  const [first, setFirst] = useState(contact?.firstName ?? initialFirst);
  const [last, setLast] = useState(contact?.lastName ?? initialLast);
  const [email, setEmail] = useState(contact?.email ?? initialEmail);
  const [phone, setPhone] = useState(contact?.phone ?? "");
  const [title, setTitle] = useState(contact?.title ?? initialTitle);
  const [isPrimary, setIsPrimary] = useState(contact?.isPrimary ?? false);
  // A building's shared inbox (Stephanie 2026-10-30): no person, no name
  // needed, and emails to it open "Hi!".
  const [buildingEmail, setBuildingEmail] = useState(isBuildingEmail(contact?.tags));
  const [error, setError] = useState<string | null>(null);
  // Locations (Bozzuto, Stephanie 2026-10-20). Adding: only a main account asks. Editing: any account of a company with
  // locations can move the person to another one.
  const locationsQ = useClientLocations(clientId);
  const company = !contact && locationsQ.data?.isMain ? locationsQ.data.company : null;
  const moveCompany = contact ? locationsQ.data?.company ?? null : null;
  const [location, setLocation] = useState(emptyLocation);
  const [moveTo, setMoveTo] = useState({ pick: clientId, newName: "" });
  // Extra locations this person also works with (multi-location contacts,
  // Stephanie 2026-10-28). Only when editing, on a company with locations,
  // once Jake's table is there.
  const linksQ = useQuery({
    queryKey: ["contacts", contact?.id, "locations"],
    queryFn: () => api<{ ready: boolean; locations: Array<{ clientId: string; name: string }> }>(`/contacts/${contact!.id}/locations`),
    enabled: !!contact && !!moveCompany,
  });
  const savedLinks = (linksQ.data?.locations ?? []).map((l) => ({ id: l.clientId, name: l.name }));
  const [links, setLinks] = useState<LinkableAccount[] | null>(null);
  useEffect(() => {
    if (links === null && linksQ.data) setLinks(savedLinks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linksQ.data]);
  const showLinks = !!moveCompany && linksQ.data?.ready === true && links !== null;

  const createMutation = useMutation({
    mutationFn: async (body: {
      first_name: string;
      last_name: string;
      email?: string | null;
      phone?: string | null;
      title?: string | null;
      is_primary?: boolean;
      tags?: string[];
      location_client_id?: string;
      new_location_name?: string;
    }) => {
      if (!contact) {
        return api<{ contact: CreatedContact }>(`/clients/${clientId}/contacts`, {
          method: "POST",
          body: JSON.stringify(body),
        });
      }
      const { location_client_id: _l, new_location_name: _n, ...fields } = body;
      const saved = await api<{ contact: CreatedContact }>(`/contacts/${contact.id}`, {
        method: "PATCH",
        // Editing: a cleared field is cleared (null), not left as it was.
        body: JSON.stringify({ ...fields, email: fields.email ?? null, phone: fields.phone ?? null, title: fields.title ?? null }),
      });
      // Moved to another location (after the other edits are saved).
      let result = saved;
      if (moveCompany && moveTo.pick && moveTo.pick !== clientId) {
        const moved = await api<{ clientId: string }>(`/contacts/${contact.id}/move-location`, {
          method: "POST",
          body: JSON.stringify(moveTo.pick === "new" ? { new_location_name: moveTo.newName.trim() } : { location_client_id: moveTo.pick }),
        });
        result = { contact: { ...saved.contact, clientId: moved.clientId } };
      }
      // The other locations they also work with (after a move, so their new
      // home is never also an extra location — the server drops it).
      if (showLinks && links && linksChanged(savedLinks.map((l) => l.id), links.map((l) => l.id))) {
        await api(`/contacts/${contact.id}/locations`, {
          method: "PUT",
          body: JSON.stringify({ client_ids: links.map((l) => l.id) }),
        });
        for (const id of new Set([...savedLinks.map((l) => l.id), ...links.map((l) => l.id)])) {
          void queryClient.invalidateQueries({ queryKey: ["clients", id, "contacts"] });
        }
        void queryClient.invalidateQueries({ queryKey: ["contacts", contact.id, "locations"] });
      }
      return result;
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: ["clients", clientId, "contacts"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["clients", clientId, "detail"],
      });
      if (result.contact.clientId && result.contact.clientId !== clientId) {
        // Landed on a location — refresh that account and the location list.
        void queryClient.invalidateQueries({ queryKey: ["clients", result.contact.clientId] });
        void queryClient.invalidateQueries({ queryKey: ["clients", clientId, "locations"] });
      }
      onCreated(result.contact);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = (err.body ?? {}) as { error?: string; detail?: string };
        if (body.error && (body as { code?: string }).code === "location_required") {
          setError(body.error);
        } else if (
          err.status === 409 ||
          body.error === "email_exists" ||
          body.error === "email already exists for this client"
        ) {
          setError(
            body.detail ?? "A contact with that email already exists.",
          );
        } else {
          setError(`Couldn't ${contact ? "save" : "create"} contact (${err.status})`);
        }
      } else {
        setError("Couldn't create contact — see console");
      }
    },
  });

  const submit = (): void => {
    // Only a first name is needed — imported contacts often have no last
    // name, and they must still be editable (Stephanie 2026-10-30).
    if (first.trim().length === 0 && !buildingEmail) {
      setError("Type a first name — or tick “Building email” for a building's shared inbox.");
      return;
    }
    if (buildingEmail && email.trim().length === 0) {
      setError("A building email needs the email address.");
      return;
    }
    const loc = locationPayload(company, location);
    if (!loc.ok) {
      setError(loc.error);
      return;
    }
    if (moveCompany && moveTo.pick === "new" && moveTo.newName.trim().length < 2) {
      setError("Type the new location's name.");
      return;
    }
    setError(null);
    createMutation.mutate({
      ...loc.body,
      first_name: buildingEmail ? "" : first.trim(),
      last_name: buildingEmail ? "" : last.trim(),
      email: email.trim().length > 0 ? email.trim() : undefined,
      phone: phone.trim().length > 0 ? phone.trim() : undefined,
      title: title.trim().length > 0 ? title.trim() : undefined,
      is_primary: isPrimary,
      tags: [...(contact?.tags ?? []).filter((t) => t !== BUILDING_EMAIL_TAG), ...(buildingEmail ? [BUILDING_EMAIL_TAG] : [])],
    });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="rounded-lg border border-peach-300 bg-peach-50 p-5"
    >
      <div className="mb-3 font-semibold text-brown-500">{heading}</div>
      <div className="grid gap-3 sm:grid-cols-2">
        {/* A building email has no name fields at all (Stephanie
            2026-10-30); last name is never required. */}
        {!buildingEmail && (
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
            First name
          </span>
          <Input
            autoFocus
            value={first}
            onChange={(e) => setFirst(e.target.value)}
            maxLength={120}
          />
        </label>
        )}
        {!buildingEmail && (
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
              Last name (optional)
            </span>
            <Input
              value={last}
              onChange={(e) => setLast(e.target.value)}
              maxLength={120}
            />
          </label>
        )}
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
            Email
          </span>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={320}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
            Phone
          </span>
          <Input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={60}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400">
            Title
          </span>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={160}
          />
        </label>
      </div>
      {company ? (
        <div className="mt-3">
          <ContactLocationSelect company={company} value={location} onChange={setLocation} />
        </div>
      ) : null}
      {moveCompany ? (
        <div className="mt-3">
          <ContactLocationSelect company={moveCompany} value={moveTo} onChange={setMoveTo} label="Location" />
          {moveTo.pick && moveTo.pick !== clientId ? (
            <span className="mt-1 block text-xs text-muted">
              Saving moves them (and their portal login) to this location. Their past orders stay where they are.
            </span>
          ) : null}
        </div>
      ) : null}
      {showLinks && moveCompany && links ? (
        <div className="mt-3">
          <AlsoWorksWith
            company={moveCompany}
            homeId={moveTo.pick && moveTo.pick !== "new" ? moveTo.pick : clientId}
            picked={links}
            onChange={setLinks}
            labelClassName="mb-1 block text-xs font-semibold uppercase tracking-wide text-brown-400"
          />
        </div>
      ) : null}
      <label className="mt-3 flex items-center gap-2 text-sm text-brown-500">
        <input
          type="checkbox"
          checked={buildingEmail}
          onChange={(e) => setBuildingEmail(e.target.checked)}
          className="h-4 w-4 rounded border-brown-300 accent-rust-500"
        />
        Building email — a shared inbox, not a person (no name needed; emails say “Hi!”)
      </label>
      <label className="mt-3 flex items-center gap-2 text-sm text-brown-500">
        <input
          type="checkbox"
          checked={isPrimary}
          onChange={(e) => setIsPrimary(e.target.checked)}
          className="h-4 w-4 rounded border-brown-300 accent-rust-500"
        />
        Mark as primary contact
      </label>
      {error && (
        <div className="mt-3 rounded-md border border-rust-200 bg-rust-50 px-3 py-2 text-sm text-rust-500">
          {error}
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <Button type="submit" disabled={createMutation.isPending}>
          {createMutation.isPending ? "Saving…" : "Save contact"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={createMutation.isPending}
        >
          Cancel
        </Button>
        {/* Delete a contact (Stephanie 2026-10-30: "it doesn't let me delete a
            contact") — the same people who can edit it; soft delete. */}
        {contact && (
          <div className="ml-auto">
            <DeleteEntityAction
              deletePath={`/contacts/${contact.id}`}
              triggerLabel="Delete contact"
              allowedRoles={["admin", "client_rep"]}
              confirmTitle="Delete this contact?"
              confirmBody={
                <>
                  This removes{" "}
                  <strong className="font-semibold text-brown-500">
                    {[contact.firstName, contact.lastName].filter(Boolean).join(" ") || "this contact"}
                  </strong>{" "}
                  from this account, its send windows and tracking emails. Past orders, proofs and emails keep their name.
                </>
              }
              redirectTo={`/clients/${clientId}/contacts`}
              flashMessage="Contact deleted."
              errorFallback="We couldn't delete this contact. Nothing was changed — try again."
              invalidateKeys={[["clients", clientId, "contacts"], ["clients", clientId, "detail"]]}
            />
          </div>
        )}
      </div>
    </form>
  );
}
